import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { apiClient, formatApiErrorDetail } from "../../lib/api";
import { Header } from "../../components/vc/Header";
import { PrimaryButton, SecondaryButton } from "../../components/vc/Buttons";
import { ArrowLeft, Download, Share2, RefreshCw, Copy, QrCode, ShieldAlert, Pencil } from "lucide-react";
import { Input } from "../../components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";
import { toast } from "sonner";

/**
 * QR Code Library (#3)
 * A permanent QR is generated per business (backed by the trader's supplier_code).
 * The user can view, download, share, and regenerate it. The QR payload embeds the
 * supplier code so scanning it links two businesses instantly.
 */
export default function VCQRLibrary() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenBusy, setRegenBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [customCode, setCustomCode] = useState("");
  const [savingCode, setSavingCode] = useState(false);
  const canvasRef = useRef(null);

  useEffect(() => {
    apiClient.get("/me/supplier-code")
      .then((r) => { setMe(r.data); setCustomCode(r.data.code); })
      .catch(() => toast.error("Could not load QR"))
      .finally(() => setLoading(false));
  }, []);

  const qrPayload = me ? `konexo://connect?code=${me.code}` : "";

  const download = () => {
    const canvas = canvasRef.current?.querySelector("canvas");
    if (!canvas) { toast.error("QR not ready"); return; }
    const url = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = url;
    a.download = `konexo-qr-${me.code}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success("QR downloaded");
  };

  const share = async () => {
    // Phase 7 #2 — include a real clickable deep link alongside the code.
    const link = `${window.location.origin}/connect/${me.code}`;
    const text = `Connect with ${me.business_name || me.name} on KonexoCRM: ${link} (code: ${me.code})`;
    // Try Web Share w/ image if possible
    const canvas = canvasRef.current?.querySelector("canvas");
    if (canvas && navigator.canShare && navigator.share) {
      try {
        canvas.toBlob(async (blob) => {
          const file = new File([blob], `konexo-qr-${me.code}.png`, { type: "image/png" });
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({ text, title: "KonexoCRM QR", files: [file] });
            return;
          }
          await navigator.share({ text });
        }, "image/png");
        return;
      } catch (_e) { /* fallthrough */ }
    }
    if (navigator.share) {
      try { await navigator.share({ text }); return; } catch (_e) { /* fallthrough */ }
    }
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Invite copied to clipboard");
    } catch { toast.error("Could not share"); }
  };

  const copyCode = async () => {
    try { await navigator.clipboard.writeText(me.code); toast.success("Code copied"); }
    catch { toast.error("Copy failed"); }
  };

  const regenerate = async () => {
    setRegenBusy(true);
    try {
      const { data } = await apiClient.post("/me/supplier-code/regenerate");
      setMe((prev) => ({ ...prev, code: data.code }));
      setCustomCode(data.code);
      toast.success("New QR generated — the old code is now invalid");
      setConfirmOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to regenerate");
    } finally {
      setRegenBusy(false);
    }
  };

  // Phase 7 #3 — max 8 chars, capital letters + numbers only, auto-uppercase.
  const onCustomCodeChange = (e) => {
    const cleaned = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 8);
    setCustomCode(cleaned);
  };

  const saveCustomCode = async () => {
    if (customCode === me.code) return;
    setSavingCode(true);
    try {
      const { data } = await apiClient.put("/me/supplier-code", { code: customCode });
      setMe((prev) => ({ ...prev, code: data.code }));
      setCustomCode(data.code);
      toast.success("Your code is now " + data.code);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to save code");
    } finally {
      setSavingCode(false);
    }
  };

  return (
    <>
      <Header />
      <div className="px-4 pt-4 pb-32 space-y-4">
        <button onClick={() => navigate(-1)} data-testid="qr-back" className="flex items-center gap-1 text-sm font-semibold text-slate-600">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div>
          <div className="inline-flex items-center gap-1.5 text-emerald-600 text-[10px] font-bold uppercase tracking-widest mb-1">
            <QrCode className="w-3 h-3" strokeWidth={2.6} /> QR Code Library
          </div>
          <h2 className="text-2xl font-bold text-slate-900 leading-tight">Your permanent QR</h2>
          <p className="text-sm text-slate-500 mt-0.5">Share it with any business to instantly connect on KonexoCRM.</p>
        </div>

        {loading ? (
          <div className="h-72 bg-white rounded-3xl border border-slate-100 animate-pulse" />
        ) : me ? (
          <>
            {/* QR Display */}
            <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 flex flex-col items-center gap-4" data-testid="qr-panel">
              <div ref={canvasRef} className="bg-white p-3 rounded-2xl border-2 border-emerald-200" data-testid="qr-canvas-wrap">
                <QRCodeCanvas
                  value={qrPayload}
                  size={220}
                  level="M"
                  includeMargin={false}
                  fgColor="#065f46"
                  bgColor="#ffffff"
                />
              </div>
              <div className="text-center space-y-0.5">
                <div className="text-sm text-slate-500">{me.business_name || me.name}</div>
                <code data-testid="qr-code-text" className="text-xl font-bold tracking-[0.35em] text-emerald-800">{me.code}</code>
              </div>
              <button
                onClick={copyCode}
                data-testid="qr-copy-code"
                className="h-9 px-4 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold flex items-center gap-1.5 transition"
              >
                <Copy className="w-3.5 h-3.5" /> Copy code
              </button>
            </div>

            {/* Actions */}
            <div className="grid grid-cols-2 gap-2">
              <SecondaryButton onClick={download} data-testid="qr-download-btn" className="flex items-center justify-center gap-2">
                <Download className="w-4 h-4" /> Download
              </SecondaryButton>
              <PrimaryButton onClick={share} data-testid="qr-share-btn" className="flex items-center justify-center gap-2">
                <Share2 className="w-4 h-4" /> Share
              </PrimaryButton>
            </div>

            {/* Customize code — Phase 7 #3 */}
            <div className="bg-white rounded-2xl border border-slate-100 p-4 space-y-2.5" data-testid="qr-customize-card">
              <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-400">
                <Pencil className="w-3 h-3" /> Customize your code
              </div>
              <div className="flex items-center gap-2">
                <Input
                  value={customCode}
                  onChange={onCustomCodeChange}
                  maxLength={8}
                  data-testid="qr-custom-code-input"
                  placeholder="ABC123"
                  className="h-11 rounded-xl font-mono tracking-widest font-bold uppercase"
                />
                <PrimaryButton
                  onClick={saveCustomCode}
                  loading={savingCode}
                  disabled={!customCode || customCode === me.code}
                  data-testid="qr-custom-code-save"
                  className="h-11 shrink-0 px-4"
                >
                  Save
                </PrimaryButton>
              </div>
              <p className="text-[11px] text-slate-400">Max 8 characters — capital letters and numbers only.</p>
            </div>

            {/* Regenerate */}
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3" data-testid="qr-regenerate-card">
              <ShieldAlert className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-bold text-amber-900">Regenerate QR</div>
                <p className="text-xs text-amber-800 mt-0.5">
                  Only regenerate if your current QR was compromised. Existing connected businesses stay linked, but any pending shares of the old code will stop working.
                </p>
                <button
                  onClick={() => setConfirmOpen(true)}
                  data-testid="qr-regenerate-btn"
                  className="mt-3 h-9 px-4 rounded-xl bg-white border border-amber-300 hover:border-amber-400 text-amber-800 text-xs font-bold flex items-center gap-1.5 transition"
                >
                  <RefreshCw className="w-3.5 h-3.5" /> Regenerate
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center text-sm text-slate-500 py-16">Could not load your QR code.</div>
        )}
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="rounded-3xl" data-testid="qr-regenerate-dialog">
          <DialogHeader>
            <DialogTitle>Regenerate your QR code?</DialogTitle>
            <DialogDescription>
              Your current code <b>{me?.code}</b> will stop working immediately. Any shared QR or invite text using the old code will no longer connect a business to you.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <SecondaryButton onClick={() => setConfirmOpen(false)} data-testid="qr-regenerate-cancel">Cancel</SecondaryButton>
            <PrimaryButton onClick={regenerate} loading={regenBusy} data-testid="qr-regenerate-confirm">
              Yes, regenerate
            </PrimaryButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
